import React from 'react'
import { colors, typography, spacing, borderRadius, shadows } from '../../../styles/design-system'

export type AccountTab = 'profile' | 'addresses' | 'security'

interface AccountSidebarProps {
  activeTab: AccountTab
  onTabChange: (tab: AccountTab) => void
  onNavigateToOrders: () => void
  onNavigateToCart: () => void
  isMobile?: boolean
}

const getButtonStyles = (isActive: boolean): React.CSSProperties => ({
  width: '100%',
  padding: spacing.lg,
  border: 'none',
  backgroundColor: isActive ? colors.secondary.lightBlue : 'transparent',
  color: isActive ? colors.primary.blue : colors.neutral.gray700,
  textAlign: 'left',
  cursor: 'pointer',
  fontSize: typography.fontSize.base,
  fontWeight: isActive ? typography.fontWeight.semibold : typography.fontWeight.medium,
  borderLeft: isActive ? `4px solid ${colors.primary.blue}` : '4px solid transparent',
  display: 'flex',
  alignItems: 'center',
  gap: spacing.md,
  transition: 'all 0.2s ease'
})

const AccountSidebar: React.FC<AccountSidebarProps> = ({
  activeTab,
  onTabChange,
  onNavigateToOrders,
  onNavigateToCart,
  isMobile = false
}) => {
  const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>, tab: AccountTab) => {
    if (activeTab !== tab) {
      event.currentTarget.style.backgroundColor = colors.neutral.gray50
    }
  }

  const handleMouseLeave = (event: React.MouseEvent<HTMLButtonElement>, tab: AccountTab) => {
    if (activeTab !== tab) {
      event.currentTarget.style.backgroundColor = 'transparent'
    }
  }

  if (isMobile) {
    return (
      <div>
        <div
          style={{
            backgroundColor: colors.neutral.white,
            borderRadius: borderRadius.xl,
            boxShadow: shadows.sm,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          <style>{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          <button
            onClick={() => onTabChange('profile')}
            style={{
              ...getButtonStyles(activeTab === 'profile'),
              flex: '1 1 auto',
              minWidth: '100px',
              padding: spacing.md,
              borderLeft: activeTab === 'profile' ? `4px solid ${colors.primary.blue}` : '4px solid transparent',
              borderBottom: activeTab === 'profile' ? `4px solid ${colors.primary.blue}` : '4px solid transparent',
              borderTop: 'none',
              borderRight: 'none',
              flexDirection: 'column',
              gap: spacing.xs
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span style={{ fontSize: typography.fontSize.xs }}>Dados</span>
          </button>

          <button
            onClick={() => onTabChange('addresses')}
            style={{
              ...getButtonStyles(activeTab === 'addresses'),
              flex: '1 1 auto',
              minWidth: '100px',
              padding: spacing.md,
              borderLeft: activeTab === 'addresses' ? `4px solid ${colors.primary.blue}` : '4px solid transparent',
              borderBottom: activeTab === 'addresses' ? `4px solid ${colors.primary.blue}` : '4px solid transparent',
              borderTop: 'none',
              borderRight: 'none',
              flexDirection: 'column',
              gap: spacing.xs
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span style={{ fontSize: typography.fontSize.xs }}>Endereços</span>
          </button>

          <button
            onClick={() => onTabChange('security')}
            style={{
              ...getButtonStyles(activeTab === 'security'),
              flex: '1 1 auto',
              minWidth: '100px',
              padding: spacing.md,
              borderLeft: activeTab === 'security' ? `4px solid ${colors.primary.blue}` : '4px solid transparent',
              borderBottom: activeTab === 'security' ? `4px solid ${colors.primary.blue}` : '4px solid transparent',
              borderTop: 'none',
              borderRight: 'none',
              flexDirection: 'column',
              gap: spacing.xs
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span style={{ fontSize: typography.fontSize.xs }}>Segurança</span>
          </button>
        </div>

        <div
          style={{
            backgroundColor: colors.neutral.white,
            borderRadius: borderRadius.xl,
            boxShadow: shadows.sm,
            padding: spacing.md,
            marginTop: spacing.md,
            display: 'flex',
            gap: spacing.sm
          }}
        >
          <button
            onClick={onNavigateToOrders}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${colors.neutral.gray200}`,
              color: colors.neutral.gray700,
              padding: spacing.sm,
              borderRadius: borderRadius.lg,
              cursor: 'pointer',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.medium,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.xs,
              flex: 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.borderColor = colors.primary.blue
              event.currentTarget.style.color = colors.primary.blue
              event.currentTarget.style.backgroundColor = colors.secondary.lightBlue
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.borderColor = colors.neutral.gray200
              event.currentTarget.style.color = colors.neutral.gray700
              event.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Pedidos
          </button>

          <button
            onClick={onNavigateToCart}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${colors.neutral.gray200}`,
              color: colors.neutral.gray700,
              padding: spacing.sm,
              borderRadius: borderRadius.lg,
              cursor: 'pointer',
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.medium,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.xs,
              flex: 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.borderColor = colors.primary.blue
              event.currentTarget.style.color = colors.primary.blue
              event.currentTarget.style.backgroundColor = colors.secondary.lightBlue
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.borderColor = colors.neutral.gray200
              event.currentTarget.style.color = colors.neutral.gray700
              event.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Carrinho
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div
        style={{
          backgroundColor: colors.neutral.white,
          borderRadius: borderRadius.xl,
          boxShadow: shadows.sm,
          overflow: 'hidden'
        }}
      >
        <button
          onClick={() => onTabChange('profile')}
          style={getButtonStyles(activeTab === 'profile')}
          onMouseEnter={(event) => handleMouseEnter(event, 'profile')}
          onMouseLeave={(event) => handleMouseLeave(event, 'profile')}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Dados Pessoais
        </button>

        <button
          onClick={() => onTabChange('addresses')}
          style={getButtonStyles(activeTab === 'addresses')}
          onMouseEnter={(event) => handleMouseEnter(event, 'addresses')}
          onMouseLeave={(event) => handleMouseLeave(event, 'addresses')}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Endereços
        </button>

        <button
          onClick={() => onTabChange('security')}
          style={getButtonStyles(activeTab === 'security')}
          onMouseEnter={(event) => handleMouseEnter(event, 'security')}
          onMouseLeave={(event) => handleMouseLeave(event, 'security')}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Segurança
        </button>
      </div>

      <div
        style={{
          backgroundColor: colors.neutral.white,
          borderRadius: borderRadius.xl,
          boxShadow: shadows.sm,
          padding: spacing.xl,
          marginTop: spacing.lg
        }}
      >
        <h3
          style={{
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            color: colors.neutral.gray900,
            margin: 0,
            marginBottom: spacing.lg
          }}
        >
          Ações Rápidas
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <button
            onClick={onNavigateToOrders}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${colors.neutral.gray200}`,
              color: colors.neutral.gray700,
              padding: spacing.md,
              borderRadius: borderRadius.lg,
              cursor: 'pointer',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.borderColor = colors.primary.blue
              event.currentTarget.style.color = colors.primary.blue
              event.currentTarget.style.backgroundColor = colors.secondary.lightBlue
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.borderColor = colors.neutral.gray200
              event.currentTarget.style.color = colors.neutral.gray700
              event.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Meus Pedidos
          </button>

          <button
            onClick={onNavigateToCart}
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${colors.neutral.gray200}`,
              color: colors.neutral.gray700,
              padding: spacing.md,
              borderRadius: borderRadius.lg,
              cursor: 'pointer',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.borderColor = colors.primary.blue
              event.currentTarget.style.color = colors.primary.blue
              event.currentTarget.style.backgroundColor = colors.secondary.lightBlue
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.borderColor = colors.neutral.gray200
              event.currentTarget.style.color = colors.neutral.gray700
              event.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Meu Carrinho
          </button>
        </div>
      </div>
    </div>
  )
}

export default AccountSidebar

